import {
  action$,
  combineReducers,
  dispatchAction,
  streamReducer,
  ofType,
} from '@ardoq/rxbeach';
import { APIEntityType, ArdoqId, ViewIds } from '@ardoq/api-types';
import { Observable, combineLatest } from 'rxjs';
import { context$ } from 'streams/context/context$';
import {
  ComponentDataSourceItem,
  ComponentItemData,
  ComponentTreeViewSettings,
} from '../types';
import { modelUpdateNotification$ } from 'modelInterface/modelUpdateNotification$';
import { componentInterface } from '@ardoq/component-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { getDataSource } from '../utils/getTreeData';
import { getComponentCssColors } from 'utils/modelCssManager/getCssColors';
import { NAVIGATOR_BACKGROUND } from '@ardoq/component-hierarchies';
import { ensureContrast } from '@ardoq/color-helpers';
import { getComponentLabelParts } from '@ardoq/graph';
import { toComponentTreeLabels } from '../utils/componentTreeLabels';
import { bypassLimitAction } from '../actions';
import { map, startWith } from 'rxjs/operators';
import { HasViewInstanceId, ViewError } from '@ardoq/graph';
import { ItemsType, nodeLimitError } from '@ardoq/error-info-box';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { formatNumber, type Locale } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';
import {
  getErrorInfoBoxLabels,
  getNodeLimitErrorLabels,
  type ErrorInfoBoxLabels,
} from 'locales/getErrorInfoBoxLabels';
import { isPresentationMode } from 'env';
import { ExtractStreamShape } from 'tabview/types';
import { setupCollapsed$ } from 'streams/setupCollapsed/setupCollapsed$';
import { getFocusedStream, getHoveredStream } from 'tabview/streams';
import { WorkspaceModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';
import { COMPONENT_TREE_COMPONENTS_LIMIT } from 'views/environmentals/viewDataLimits';

const VIEW_ID = ViewIds.COMPONENTTREE;

const { collapsed$, toggleCollapse, setCollapsed } = setupCollapsed$(VIEW_ID);

export const emptyState: ComponentTreeViewModel = {
  viewSettings: getDefaultViewSettings(ViewIds.COMPONENTTREE, false),
  sourceData: [],
  sourceDataWithRenderingData: [],
  collapsed: {},
  collapsibleIds: [],
  focusedComponentId: null,
  hoveredComponentId: null,
  selectedComponentId: null,
  bypassLimit: false,
  errors: [],
  errorInfoBoxLabels: getErrorInfoBoxLabels(DEFAULT_ARDOQ_I18NLOCALE),
};

type ResetSourceData = (
  previousState: ComponentTreeViewModel,
  args: [
    ComponentTreeViewSettings,
    ExtractStreamShape<typeof context$>,
    Record<ArdoqId, boolean>,
    boolean,
    null,
    I18nLocale,
    Locale,
  ]
) => ComponentTreeViewModel;

type SourceDataShape = {
  viewSettings: ComponentTreeViewSettings;
  sourceData: ComponentDataSourceItem[];
  sourceDataWithRenderingData: ComponentDataSourceItem[];
  collapsed: Record<ArdoqId, boolean>;
  collapsibleIds: ArdoqId[];
  errors: ViewError[];
  bypassLimit: boolean;
  errorInfoBoxLabels: ErrorInfoBoxLabels;
};

type NodesAffectedByItneractiveStates = {
  selectedComponentId: ArdoqId | null;
  focusedComponentId: ArdoqId | null;
  hoveredComponentId: ArdoqId | null;
};

type ComponentTreeViewModel = SourceDataShape &
  NodesAffectedByItneractiveStates;

type ReplaceItemIfItIsInteractedArgs = {
  item: ComponentDataSourceItem;
  focusedComponentId: string | null;
  hoveredComponentId: string | null;
  selectedComponentId: string | null;
  prevFocusedComponentId?: string | null;
};
const enhanceItemDataWithInteractiveStates = ({
  item,
  focusedComponentId,
  hoveredComponentId,
  selectedComponentId,
  prevFocusedComponentId,
}: ReplaceItemIfItIsInteractedArgs): ComponentDataSourceItem => {
  const isFocused = item.id === focusedComponentId;
  const isLatestFocused = item.id === prevFocusedComponentId;
  const isSelected = item.id === selectedComponentId;
  let isHovered = item.id === hoveredComponentId;

  if (!isFocused && !isHovered && !isSelected) {
    return item;
  }

  // If latest unfocused unhover too
  if (isLatestFocused && !isFocused) {
    isHovered = false;
  }

  return {
    ...item,
    isFocused,
    isHovered,
    isSelected,
  };
};

const getWorkspaceItemData = (workspaceId: ArdoqId): ComponentItemData => {
  const workspaceData = workspaceInterface.getWorkspaceData(workspaceId);
  return {
    id: workspaceId,
    type: APIEntityType.WORKSPACE,
    name: workspaceData?.name || '',
    representationData: null,
    displayLabel: null,
    fullLabel: null,
    classNames: 'workspace',
  };
};

const getComponentItemData = (componentId: ArdoqId): ComponentItemData => {
  const componentData = componentInterface.get(componentId);

  const { fill } = getComponentCssColors(componentId, {
    useAsBackgroundStyle: false,
  }) ?? { fill: undefined };

  const componentColors = getComponentCssColors(componentId, {
    useAsBackgroundStyle: true,
  }) ?? { fill: undefined, stroke: undefined };

  const iconColor = ensureContrast(NAVIGATOR_BACKGROUND, fill ?? 'black');
  const { displayLabel, fullLabel } = toComponentTreeLabels(
    getComponentLabelParts(componentId)
  );

  return {
    id: componentId,
    type: APIEntityType.COMPONENT,
    name: componentData?.name || '',
    representationData: componentInterface.getRepresentationData(
      componentId
    ) ?? {
      isImage: false,
      value: null,
    },
    displayLabel,
    fullLabel,
    iconStyle: { color: iconColor },
    componentStyle: {
      backgroundColor: componentColors?.fill,
      borderColor: componentColors?.stroke,
    },
    classNames: 'component',
  };
};

const buildItemDataMap = (
  hierarchy: Record<ArdoqId, unknown>,
  workspaceIds: ArdoqId[]
): Record<ArdoqId, ComponentItemData> => {
  const workspaceIdSet = new Set(workspaceIds);
  return Object.keys(hierarchy).reduce<Record<ArdoqId, ComponentItemData>>(
    (acc, id) => {
      if (id === 'root') return acc;
      acc[id] = workspaceIdSet.has(id)
        ? getWorkspaceItemData(id)
        : getComponentItemData(id);
      return acc;
    },
    {}
  );
};

export const resetComponentSourceData: ResetSourceData = (
  previousState,
  [viewSettings, context, collapsed, isBypassLimit, , i18nLocale, locale]
) => {
  if (!context.workspaceId) {
    return {
      ...emptyState,
      errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
    };
  }

  const bypassLimit =
    previousState.bypassLimit || isBypassLimit || isPresentationMode();

  const hierarchy = componentInterface.getComponentsHierarchyByWorkspaceIds(
    context.workspaceIds
  );

  const allComponentsCount =
    // minus number of workspaceIds + 'root' key
    Object.keys(hierarchy).length -
    (context.workspaceIds.filter(id => hierarchy[id]).length + 1);

  const shouldLimitItems =
    allComponentsCount > COMPONENT_TREE_COMPONENTS_LIMIT && !bypassLimit;

  const itemDataMap = buildItemDataMap(hierarchy, context.workspaceIds);

  const sourceData = getDataSource(
    collapsed,
    hierarchy,
    itemDataMap,
    shouldLimitItems ? COMPONENT_TREE_COMPONENTS_LIMIT : Infinity
  );

  const collapsibleIds = sourceData.reduce((acc: string[], { id }) => {
    if (hierarchy[id].children && id !== 'root') {
      acc.push(id);
    }

    return acc;
  }, []);

  const errors: ViewError[] = shouldLimitItems
    ? [
        nodeLimitError({
          itemCount: allComponentsCount,
          limit: COMPONENT_TREE_COMPONENTS_LIMIT,
          itemsType: ItemsType.COMPONENTS,
          formatNumber: value => formatNumber(value, 'default', locale),
          onProceedAnyway: () => dispatchAction(bypassLimitAction()),
          ...getNodeLimitErrorLabels(i18nLocale),
        }),
      ]
    : [];

  const { focusedComponentId, hoveredComponentId } = previousState;
  const selectedComponentId = context.componentId;

  return {
    viewSettings,
    sourceData,
    sourceDataWithRenderingData: sourceData.map(item =>
      enhanceItemDataWithInteractiveStates({
        item,
        focusedComponentId,
        hoveredComponentId,
        selectedComponentId,
      })
    ),
    collapsed,
    collapsibleIds,
    bypassLimit,
    errors,
    focusedComponentId,
    hoveredComponentId,
    selectedComponentId,
    errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
  };
};
const setInteractiveComponentStates = (
  previousState: ComponentTreeViewModel,
  {
    focusedComponentId,
    hoveredComponentId,
  }: { focusedComponentId: string | null; hoveredComponentId: string | null }
): ComponentTreeViewModel => {
  const cleanSourceDataItems = previousState.sourceData;
  const selectedComponentId = previousState.selectedComponentId;

  const sourceDataWithUpdatedInteractiveStates = cleanSourceDataItems.map(
    item =>
      enhanceItemDataWithInteractiveStates({
        item,
        focusedComponentId,
        hoveredComponentId,
        selectedComponentId,
        prevFocusedComponentId: previousState.focusedComponentId,
      })
  );

  return {
    ...previousState,
    sourceDataWithRenderingData: sourceDataWithUpdatedInteractiveStates,
    focusedComponentId,
    hoveredComponentId,
  };
};

const interactiveComponentStates$ = combineLatest([
  getFocusedStream(VIEW_ID),
  getHoveredStream(VIEW_ID),
]).pipe(
  map(([focusedComponentId, hoveredComponentId]) => ({
    focusedComponentId,
    hoveredComponentId,
  }))
);

export type GetComponentTreeViewModelArgs = HasViewInstanceId & {
  viewSettingsWithChanges$: Observable<
    WorkspaceModeInstanceViewSettings<ViewIds.COMPONENTTREE>
  >;
};
export const getViewModel$ = ({
  viewSettingsWithChanges$,
}: GetComponentTreeViewModelArgs) => {
  const viewSettings$ = viewSettingsWithChanges$.pipe(
    map(({ currentSettings }) => currentSettings)
  );
  const sourceData$ = combineLatest([
    viewSettings$,
    context$,
    collapsed$,
    action$.pipe(ofType(bypassLimitAction), startWith(false), map(Boolean)),
    modelUpdateNotification$,
    i18nLocale$,
    locale$,
  ]);

  const baseViewModel$ = action$.pipe(
    combineReducers<ComponentTreeViewModel>(emptyState, [
      streamReducer(sourceData$, resetComponentSourceData),
      streamReducer(interactiveComponentStates$, setInteractiveComponentStates),
    ])
  );

  return combineLatest([baseViewModel$, i18nLocale$]).pipe(
    map(([viewModel, i18nLocale]) => ({ ...viewModel, i18nLocale }))
  );
};

export { toggleCollapse, setCollapsed };
